import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  TextField,
  InputAdornment,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  LockReset as LockResetIcon,
  ContentCopy as CopyIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { User } from '../../../types/user.types';
import { UsersServiceHelpers } from '../../../utils/api-helpers/UsersService.helpers';
import { extractErrorMessage } from '../../../utils/errorHelpers';

interface UserResetPasswordModalProps {
  open: boolean;
  user: User | null;
  onClose: () => void;
  onSuccess: () => void;
}

const UserResetPasswordModal: React.FC<UserResetPasswordModalProps> = ({
  open,
  user,
  onClose,
  onSuccess,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{
    temporaryPassword: string;
    expiresAt: string;
  } | null>(null);
  const [copied, setCopied] = useState(false);

  const handleReset = async () => {
    if (!user) return;
    setIsLoading(true);
    setError(null);

    try {
      const response = await UsersServiceHelpers.resetPassword(user.id);
      setResult(response);
    } catch (err) {
      setError(extractErrorMessage(err, 'Error al restablecer la contraseña.'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result.temporaryPassword);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = result.temporaryPassword;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleClose = () => {
    if (result) onSuccess();
    setResult(null);
    setError(null);
    setCopied(false);
    onClose();
  };

  if (!user) return null;

  const formatExpiry = (iso: string) => {
    const date = new Date(iso);
    return date.toLocaleString('es', {
      hour: '2-digit',
      minute: '2-digit',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LockResetIcon sx={{ color: '#10b981' }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Restablecer Contraseña
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ p: 1 }}>
          <Box
            sx={{
              p: 2,
              mb: 2,
              borderRadius: 1,
              backgroundColor: '#f9fafb',
              border: '1px solid #e5e7eb',
            }}
          >
            <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 500 }}>
              Usuario
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {user.email}
            </Typography>
            <Typography variant="body2" sx={{ color: '#6b7280' }}>
              {`${user.firstName} ${user.lastName}`.trim()}
            </Typography>
          </Box>

          {!result && !error && !isLoading && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Se generará una contraseña temporal. El usuario deberá cambiarla en su próximo inicio
              de sesión. Las sesiones activas serán invalidadas.
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          )}

          {result && (
            <>
              <Alert severity="success" sx={{ mb: 2 }}>
                Contraseña restablecida con éxito. Comparta esta contraseña temporal con el usuario
                de forma segura.
              </Alert>

              <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600, color: '#374151' }}>
                Contraseña Temporal
              </Typography>
              <TextField
                fullWidth
                value={result.temporaryPassword}
                InputProps={{
                  readOnly: true,
                  sx: {
                    fontFamily: 'monospace',
                    fontSize: '1.25rem',
                    fontWeight: 700,
                    letterSpacing: '0.1em',
                  },
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleCopy} color={copied ? 'success' : 'default'}>
                        {copied ? <CheckIcon /> : <CopyIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />

              <Typography variant="body2" sx={{ color: '#6b7280' }}>
                <strong>Válida hasta:</strong> {formatExpiry(result.expiresAt)}
              </Typography>
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {!result ? (
          <>
            <Button
              variant="tonal"
              onClick={handleClose}
              disabled={isLoading}
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                borderRadius: '8px',
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleReset}
              disabled={isLoading}
              startIcon={<LockResetIcon />}
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                borderRadius: '8px',
                background: 'linear-gradient(to right, #06b6d4, #10b981)',
                '&:hover': { background: 'linear-gradient(to right, #0891b2, #059669)' },
              }}
            >
              {isLoading ? 'Restableciendo...' : 'Restablecer Contraseña'}
            </Button>
          </>
        ) : (
          <Button
            onClick={handleClose}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              borderRadius: '8px',
              border: '1px solid',
              borderColor: 'divider',
              color: 'text.primary',
              bgcolor: 'background.paper',
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            Cerrar
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default UserResetPasswordModal;
